import { makeRequest } from '@humanspace/infrastructure';

// Higher Ed Assessments
export const runAssessment = async (projectId, payload) =>
  makeRequest({
    path: `/projects/${projectId}/higher-ed/assess`,
    method: 'POST',
    body: payload,
    json: true,
  });

export const getAssessments = async projectId =>
  makeRequest({
    path: `/projects/${projectId}/higher-ed/assessments`,
  });

// Top-N features ranked by β × opportunity for the latest assessment.
export const getTopFeatures = async (projectId, topN = 5) =>
  makeRequest({
    path: `/projects/${projectId}/higher-ed/top-features?topN=${topN}`,
  });

// Full feature catalog grouped by capability. Used by the Evidence page.
export const getFeatures = async () =>
  makeRequest({
    path: '/higher-ed/features',
  });

// Update the Project row from the Higher Ed project-info page.
//
// IMPORTANT: makeRequest does NOT throw on HTTP errors -- it returns the
// parsed body with _httpOk: false set on non-2xx responses. Without an
// explicit check here, the save handler's try/catch never fires, the user
// sees "save succeeded", and the value is silently dropped. Wrap the call
// to throw on non-2xx so the caller's error path actually runs.
//
// Bug history: prior to 2026-06-27 the backend had no PUT /projects/{id}
// route at all -- every save 404'd silently. ProjectsController::putProjectAction
// (v2.1, commit 23d13b4) added the route; this throw-on-error wrapper
// (same commit window) ensures future failures surface immediately.
export const updateProject = async (projectId, payload) => {
  const result = await makeRequest({
    path: `/projects/${projectId}`,
    method: 'PUT',
    body: payload,
    json: true,
  });
  if (result && result._httpOk === false) {
    const status = result._httpStatus || 'unknown';
    const message =
      (result.error && result.error.message)
      || result.message
      || `Save failed (HTTP ${status})`;
    const err = new Error(message);
    err.httpStatus = status;
    throw err;
  }
  return result;
};

// ─────────────────────────────────────────────────────────────
// Higher Ed Surveys -- Block A student-rating collection.
// Backend phase 1 (commit 02fabb8): HigherEdSurvey + HigherEdSurveyAnswer
// entities, public-link respondent flow.
// ─────────────────────────────────────────────────────────────

// Shared throw-on-error wrapper -- same pattern as updateProject above.
const throwIfHttpError = (result, defaultMessage) => {
  if (result && result._httpOk === false) {
    const status = result._httpStatus || 'unknown';
    const message =
      (result.error && result.error.message)
      || result.message
      || `${defaultMessage} (HTTP ${status})`;
    const err = new Error(message);
    err.httpStatus = status;
    throw err;
  }
  return result;
};

export const getHigherEdSurveys = async projectId => {
  const result = await makeRequest({
    path: `/projects/${projectId}/higher-ed-surveys`,
  });
  return throwIfHttpError(result, 'Failed to load surveys');
};

export const createHigherEdSurvey = async (projectId, surveyName) => {
  const result = await makeRequest({
    path: `/projects/${projectId}/higher-ed-surveys`,
    method: 'POST',
    body: { name: surveyName },
    json: true,
  });
  return throwIfHttpError(result, 'Failed to create survey');
};

export const getHigherEdSurveyDetails = async surveyId => {
  const result = await makeRequest({
    path: `/higher-ed-surveys/${surveyId}`,
  });
  return throwIfHttpError(result, 'Failed to load survey');
};

// Public endpoint -- no auth required. Backend allows anonymous on
// /api/higher-ed-surveys/.../answers (see security.yaml).
// Body must be wrapped in { capabilitiesValues: {...} } to match the
// Symfony NewHigherEdSurveyAnswerType form, which exposes a nested
// 'capabilitiesValues' field. Sending the values flat causes the form
// to silently bind an empty embedded VO -- the answer persists with all
// zeros and the aggregated averages show 0 everywhere.
export const answerHigherEdSurvey = async (surveyId, capabilitiesValues) => {
  const result = await makeRequest({
    path: `/higher-ed-surveys/${surveyId}/answers`,
    method: 'POST',
    body: { capabilitiesValues },
    json: true,
  });
  return throwIfHttpError(result, 'Failed to submit response');
};

export const removeHigherEdSurvey = async surveyId => {
  const result = await makeRequest({
    path: `/higher-ed-surveys/${surveyId}`,
    method: 'DELETE',
  });
  return throwIfHttpError(result, 'Failed to delete survey');
};
